import _ from 'lodash';

import type {DataConfig} from '@sail/data';

import RestLinkError from './RestLinkError';
import type {UnionObjectTypesMap} from './types';

export type PossibleTypes = NonNullable<
  NonNullable<DataConfig['graphql']>['schemas']['graphql']
>['possibleTypes'];

const convertValue = (
  unionObjectTypesMap: UnionObjectTypesMap,
  possibleTypes: PossibleTypes,
  value: any,
  converter: (
    unionObjectTypesMap: UnionObjectTypesMap,
    possibleTypes: PossibleTypes,
    data: any,
  ) => any,
) => {
  if (_.isPlainObject(value)) {
    return converter(unionObjectTypesMap, possibleTypes, value);
  }

  if (_.isArray(value)) {
    return value.map((v) => converter(unionObjectTypesMap, possibleTypes, v));
  }

  return value;
};

/*
 * Takes a response data object and recursively:
 * 1. Checks if it has an `object` field
 * 2. Checks `__typename` to see if it's a Union type
 * 3. Looks up unionObjectTypesMap to get the possible list of object types for the union
 * 4. Gets the discrete GraphQL type for the object type.
 * 5. Checks if the GraphQL type from step 4. is a member of the Union
 * 6. Checks if the object is also deletable resource
 * 7. Replaces `__typename` with the discrete GraphQL type.
 */
export const mapUnionTypenames = (
  unionObjectTypesMap: UnionObjectTypesMap,
  possibleTypes: PossibleTypes,
  data: any,
) => {
  // We recursively call `mapUnionTypenames` on arrays and some arrays
  // may be partially populated with nulls, so we defensively check and
  // return null rather than trying to expand.
  if (!data) {
    return data;
  }

  const {object, deleted, __typename} = data;
  if (possibleTypes[__typename]) {
    if (object == null) {
      // Skip union type narrowing for data with only an `id`, and just
      // leave the union typename (go/skip-id-only-expands)
      const fields = Object.keys(data);
      if (fields.every((field) => field === 'id' || field === '__typename')) {
        // Hack -- Apollo requires a valid typename for the field, so just
        // return the first one.
        data.__typename = possibleTypes[__typename][0];
        return data;
      }
      throw new RestLinkError(
        `The union type "${__typename}" requires an \`object\` property but data was provided without it`,
      );
    }

    const unionObjectTypes = unionObjectTypesMap[__typename];
    const objectTypes = unionObjectTypes
      ? // NOTE(keanu): /v1/search sometimes returns results with object: 'payment' for charges.
        // This should not happen, but for now, treat them as the same object: 'charge' so
        // search results can be rendered without error. See RUN_SEARCH-9353.
        unionObjectTypes[object === 'payment' ? 'charge' : object]
      : null;
    if (objectTypes) {
      const [resource, deletedResource] = objectTypes;
      const typename = deleted ? deletedResource : resource;

      if (possibleTypes[__typename].includes(typename)) {
        data.__typename = typename;
      }
    }
  }

  if (typeof data !== 'object') {
    return data;
  }

  return _.isArray(data)
    ? data.map((value) =>
        convertValue(
          unionObjectTypesMap,
          possibleTypes,
          value,
          mapUnionTypenames,
        ),
      )
    : Object.keys(data).reduce<Record<string, any>>(
        (acc, key) => ({
          ...acc,
          [key]: convertValue(
            unionObjectTypesMap,
            possibleTypes,
            data[key],
            mapUnionTypenames,
          ),
        }),
        {},
      );
};
